// src/components/clients/PipelineView.tsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  Stack,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { shadows } from "../../theme/theme";
import { formatLeadTimeSince } from "../../lib/dashboardLeads";
import type { Client, ClientStatus } from "./types";
import { statusConfig, pipelineStatuses } from "./config";

/**
 * Color the "last contacted" caption based on age. Operators glance the Kanban
 * board for clients aging out of contact — grey for fresh (<7d) · amber for
 * stale (7-14d) · red for cold (>14d). Null `last_contacted_at` (never
 * contacted) hides the caption entirely.
 */
const lastContactedColor = (
  lastContactedAt: string | null | undefined
): string | null => {
  if (!lastContactedAt) return null;
  const ms = Date.now() - new Date(lastContactedAt).getTime();
  if (Number.isNaN(ms)) return null;
  const days = ms / (1000 * 60 * 60 * 24);
  if (days < 7) return "#94a3b8";       // grey · fresh
  if (days < 14) return "#F59E0B";      // amber · stale
  return "#EF4444";                      // red · cold
};

interface PipelineViewProps {
  clients: Client[];
  onViewDetails: (client: Client) => void;
  onStatusChange: (client: Client, newStatus: ClientStatus) => void;
}

const PipelineView: React.FC<PipelineViewProps> = ({
  clients,
  onViewDetails,
  onStatusChange,
}) => {
  const theme = useTheme();
  const [draggedClientId, setDraggedClientId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<ClientStatus | null>(
    null
  );

  const clientsByStatus = useMemo(() => {
    const grouped: Record<ClientStatus, Client[]> = {
      new_client: [],
      touring: [],
      applied: [],
      payment_received: [],
      closed: [],
      out_of_contact: [],
    };
    clients.forEach((client) => {
      if (grouped[client.status]) {
        grouped[client.status].push(client);
      }
    });
    return grouped;
  }, [clients]);

  const draggedClient = useMemo(
    () => clients.find((client) => client.id === draggedClientId) || null,
    [clients, draggedClientId]
  );

  const buildClientMeta = (client: Client) => {
    const budget = client.max_budget
      ? `$${client.max_budget.toLocaleString()}`
      : "";
    const areas = (client.preferred_areas || [])
      .map((area) => area?.trim())
      .filter(Boolean);
    const areaSummary =
      areas.length > 0
        ? `${areas.slice(0, 2).join(", ")}${
            areas.length > 2 ? ` +${areas.length - 2}` : ""
          }`
        : "";

    return [budget, areaSummary].filter(Boolean).join(" · ") || client.email;
  };

  const handleDragStart =
    (client: Client) => (event: React.DragEvent<HTMLElement>) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", client.id);
      setDraggedClientId(client.id);
      setDragOverStatus(client.status);
    };

  const handleDragEnd = () => {
    setDraggedClientId(null);
    setDragOverStatus(null);
  };

  const handleColumnDragOver =
    (status: ClientStatus) => (event: React.DragEvent<HTMLElement>) => {
      if (!draggedClientId) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOverStatus(status);
    };

  const handleColumnDragLeave =
    (status: ClientStatus) => (event: React.DragEvent<HTMLElement>) => {
      if (!draggedClientId) return;
      const nextTarget = event.relatedTarget;
      if (
        nextTarget instanceof Node &&
        event.currentTarget.contains(nextTarget)
      ) {
        return;
      }
      if (dragOverStatus === status) {
        setDragOverStatus(draggedClient?.status ?? null);
      }
    };

  const handleColumnDrop =
    (status: ClientStatus) => (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();

      const droppedClientId =
        draggedClientId || event.dataTransfer.getData("text/plain");
      const droppedClient =
        clients.find((client) => client.id === droppedClientId) || null;

      setDragOverStatus(null);
      setDraggedClientId(null);

      if (!droppedClient || droppedClient.status === status) {
        return;
      }

      onStatusChange(droppedClient, status);
    };

  return (
    <Box sx={{ overflowX: "auto", pb: 2 }}>
      <Box sx={{ display: "flex", gap: 2, minWidth: "max-content" }}>
        {pipelineStatuses.map((status) => {
          const statusInfo = statusConfig[status];
          const statusClients = clientsByStatus[status];

          return (
            <Paper
              key={status}
              onDragOver={handleColumnDragOver(status)}
              onDragLeave={handleColumnDragLeave(status)}
              onDrop={handleColumnDrop(status)}
              sx={{
                width: 300,
                minHeight: 400,
                display: "flex",
                flexDirection: "column",
                backgroundColor: alpha(statusInfo.color, 0.02),
                border:
                  dragOverStatus === status && draggedClient?.status !== status
                    ? `1px dashed ${alpha(statusInfo.color, 0.75)}`
                    : "1px solid transparent",
                boxShadow:
                  dragOverStatus === status && draggedClient?.status !== status
                    ? `0 0 0 2px ${alpha(statusInfo.color, 0.15)}`
                    : "none",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              {/* Column Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `3px solid ${statusInfo.color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ color: statusInfo.color }}>{statusInfo.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {statusInfo.label}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={statusClients.length}
                  sx={{
                    backgroundColor: alpha(statusInfo.color, 0.15),
                    color: statusInfo.color,
                    fontWeight: 600,
                  }}
                />
              </Box>

              {/* Client Cards */}
              <Box sx={{ p: 1, flex: 1, overflowY: "auto" }}>
                <Stack spacing={1}>
                  {statusClients.map((client) => (
                    <Card
                      key={client.id}
                      draggable
                      onDragStart={handleDragStart(client)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.2s",
                        opacity: draggedClientId === client.id ? 0.45 : 1,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: shadows.card,
                        },
                      }}
                      onClick={() => onViewDetails(client)}
                    >
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Box display="flex" alignItems="flex-start" gap={1.25}>
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {client.first_name} {client.last_name}
                            </Typography>
                            {/* Meta + last-contacted on a single row · meta
                                left-aligned (budget + areas truncates if needed) ·
                                last-contacted right-aligned (never truncates · age-
                                colored grey/amber/red so the operator can glance
                                the board for clients going cold). */}
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ mt: 0.25, minWidth: 0 }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ flex: 1, minWidth: 0 }}
                              >
                                {buildClientMeta(client)}
                              </Typography>
                              {(() => {
                                const color = lastContactedColor(
                                  client.last_contacted_at
                                );
                                if (!color || !client.last_contacted_at) {
                                  return null;
                                }
                                const since = formatLeadTimeSince(
                                  client.last_contacted_at
                                );
                                return (
                                  <Tooltip
                                    title={`Last contacted: ${since}`}
                                    placement="left"
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color,
                                        flexShrink: 0,
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {since}
                                    </Typography>
                                  </Tooltip>
                                );
                              })()}
                            </Stack>
                          </Box>
                          {client.priority === "urgent" && (
                            <WarningIcon
                              sx={{ color: "#EF4444", fontSize: 18 }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  {statusClients.length === 0 && (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body2" color="text.secondary">
                        {draggedClient && draggedClient.status !== status
                          ? "Drop client here"
                          : "No clients"}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default PipelineView;
